import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Field, Form } from 'formik';
import { Form as AntForm, Input } from 'antd';
import { TextInput } from '@components/form';
import OperatorField from './OperatorField';
import { Button, Row, Col, Typography } from 'antd';

import { NEST_SEPARATOR } from './convertSchema';

import get from 'lodash-es/get';
import startCase from 'lodash-es/startCase';

const { Title, Text } = Typography;

function calcValue(operator, first, second) {
  const firstVal = isNaN(first) ? 0 : Number(first);
  const secondVal = isNaN(second) ? 0 : Number(second);

  if (operator === '-') {
    return firstVal - secondVal;
  }

  return firstVal + secondVal;
}

function PricingForm(props) {
  const { validationSchema, initialValues, sections, onNext, onPrev } = props;

  const handleSubmit = (values, actions) => {
    const { setSubmitting } = actions;
    setSubmitting(true);
    onNext(values);
    setSubmitting(false);
  };

  const renderRow = (type, groupName, setFieldValue, values) => {
    const firstKey = `${groupName}${NEST_SEPARATOR}${type}${NEST_SEPARATOR}first`;
    const secondKey = `${groupName}${NEST_SEPARATOR}${type}${NEST_SEPARATOR}second`;
    const fieldKey = `${groupName}${NEST_SEPARATOR}${type}${NEST_SEPARATOR}field`;
    const finalKey = `${groupName}${NEST_SEPARATOR}${type}${NEST_SEPARATOR}value`;

    const firstVal = get(values, firstKey);
    const secondVal = get(values, secondKey);
    const finalVal = get(values, finalKey);
    const fieldVal = get(values, fieldKey);

    const suffix = type === 'time' ? 'm' : undefined;
    const prefix = type === 'time' ? undefined : '$';

    return (
      <React.Fragment>
        <Row gutter={8}>
          <Col span={6}>
            <AntForm.Item>
              <Input
                value={firstVal}
                readOnly
                disabled
                suffix={suffix}
                prefix={prefix}
              />
            </AntForm.Item>
          </Col>
          <Col span={4}>
            <Field
              component={OperatorField}
              name={fieldKey}
              onChange={value => {
                setFieldValue(fieldKey, value);
                setFieldValue(finalKey, calcValue(value, firstVal, secondVal));
              }}
            />
          </Col>
          <Col span={6}>
            <Field
              component={TextInput}
              name={secondKey}
              type="number"
              suffix={suffix}
              prefix={prefix}
              onChange={e => {
                const value = e.target.value;
                setFieldValue(secondKey, value);
                setFieldValue(finalKey, calcValue(fieldVal, firstVal, value));
              }}
            />
          </Col>
          <Col span={2}>
            <Row type="flex" justify="center">
              <Text>=</Text>
            </Row>
          </Col>
          <Col span={6}>
            <AntForm.Item>
              <Input
                value={finalVal}
                readOnly
                suffix={suffix}
                prefix={prefix}
                disabled
              />
            </AntForm.Item>
          </Col>
        </Row>
      </React.Fragment>
    );
  };

  const renderSection = (section, setFieldValue, values) => {
    const { sectionkey, title, labels } = section;
    return (
      <React.Fragment key={sectionkey}>
        <Row gutter={64}>
          <Col span={4}>
            <Title level={4}>{title}</Title>
          </Col>
          <Col span={8}>
            <Row gutter={8}>
              <Col span={18}>
                <Text>Default </Text>
              </Col>
              <Col span={6}>
                <Text>Property </Text>
              </Col>
            </Row>
          </Col>
          <Col span={9}>
            <Row gutter={8}>
              <Col span={2}></Col>
              <Col span={16}>Default</Col>
              <Col span={6}>
                <Text>Property </Text>
              </Col>
            </Row>
          </Col>
        </Row>
        {labels.map(({ label, groupKey }) => (
          <React.Fragment key={`hotel-style-${label}`}>
            <Row type="flex" justify="start" align="middle" gutter={64}>
              <Col span={3}>
                <Text>{label}</Text>
              </Col>
              <Col span={9}>
                {renderRow('time', groupKey, setFieldValue, values)}
              </Col>
              <Col span={9}>
                {renderRow('price', groupKey, setFieldValue, values)}
              </Col>
            </Row>
          </React.Fragment>
        ))}
        <div style={{ minHeight: 16 }}> </div>
      </React.Fragment>
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      isInitialValid={validationSchema.isValidSync(initialValues)}
      render={({ isValid, isSubmitting, setFieldValue, values }) => {
        return (
          <Form className="form-compact">
            <Row gutter={16}>
              <Col span={7}></Col>
              <Col span={9}>
                <Title level={4}>Time</Title>
              </Col>
              <Col span={4}>
                <Title level={4}>Price ( cents) </Title>
              </Col>
            </Row>
            {sections.map(section =>
              renderSection(section, setFieldValue, values)
            )}
            <Row type="flex" className="action-btns">
              {onPrev && (
                <Button type="dashed" onClick={onPrev} disabled={isSubmitting}>
                  Prev
                </Button>
              )}
              <Button
                type="primary"
                htmlType="submit"
                disabled={!isValid || isSubmitting}
              >
                Next
              </Button>
            </Row>
          </Form>
        );
      }}
    />
  );
}

PricingForm.propTypes = {
  validationSchema: PropTypes.object,
  initialValues: PropTypes.object,
  sections: PropTypes.array,
  title: PropTypes.string,
  onNext: PropTypes.func,
  onPrev: PropTypes.func
};

export default PricingForm;
